import { FC, useEffect, useRef, useState } from "react";

import { useDebounce } from "ui";
import FilterRow from "../components/generic-table/filter-row";
import DashboardContentLayout from "../components/dashboard-content-layout";

import { SortOptions } from "../components/generic-table/generic-table";
import {
  Nullable,
  useDriverControllerGetAll,
  useDriverControllerGetAllCsv,
} from "service";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { usePersistedSearchParams } from "../../../hooks/use-persisted-search-params";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { AdRoutes } from "../../../constants/ad-routes";
import DriverManagementTable from "./driver-management-table";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import HeaderStandardActions from "../../../shared/components/header-items/HeaderStandardActions";
import CredentialsBox from "../../../components/credentials-box/credentials-box";
import EmptyState, {
  noResultsEmptyState,
} from "@/shared/components/EmptyState";
import { useListEmptyState } from "@/shared/hooks/useListEmptyState";

interface LocationState {
  showCredentialsDialog?: boolean;
  credentials?: {
    username: string;
    password: string;
  };
  isPasswordUpdate?: boolean;
}

const DriverManagementPage: FC = () => {
  const [params, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [page, setPage] = useState<number>(
    params.get("page") ? parseInt(params.get("page") as string) : 1,
  );
  const [search, setSearch] = useState<string>(params.get("search") || "");
  const [limit, setLimit] = useState<number>(
    params.get("limit") ? parseInt(params.get("limit") as string) : 50,
  );

  // Persist search params for breadcrumb navigation
  usePersistedSearchParams();

  const [csvDownloadRequested, setCsvDownloadRequested] = useState(false);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [credentials, setCredentials] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [isPasswordUpdate, setIsPasswordUpdate] = useState(false);

  const { debounce } = useDebounce(475);
  const navigate = useNavigate();

  const onSearch = (searchValue: string) => {
    debounce(() => {
      setSearch(searchValue);
      // Reset to page 1 when search changes
      setPage(1);
      // Note: FilterRow already updates the 'search' param in the URL
    });
  };

  const [sortOptions, setSortOptions] = useState<Nullable<SortOptions>>(
    params.get("sortBy")
      ? {
          sortBy: params.get("sortBy") || "",
          direction: (params.get("direction") || "") as "asc" | "desc",
        }
      : null,
  );

  // Track previous values to avoid unnecessary URL updates on mount
  const prevPageRef = useRef(page);
  const prevLimitRef = useRef(limit);

  // Sync page and limit changes to URL (only when they actually change)
  useEffect(() => {
    // Skip if values haven't changed from their previous state
    if (prevPageRef.current === page && prevLimitRef.current === limit) {
      return;
    }
    prevPageRef.current = page;
    prevLimitRef.current = limit;

    setSearchParams((prev) => {
      if (page > 1) {
        prev.set("page", page.toString());
      } else {
        prev.delete("page");
      }
      if (limit !== 50) {
        prev.set("limit", limit.toString());
      } else {
        prev.delete("limit");
      }
      return prev;
    });
  }, [page, limit, setSearchParams]);

  const { isLoading, isError, data, refetch } = useDriverControllerGetAll({
    queryParams: {
      limit,
      search,
      page,
      sort: sortOptions?.sortBy as
        | "performanceTime"
        | "lastPlow"
        | "lastUpdated",
      order: sortOptions?.direction,
    },
  });

  const {
    refetch: execDownload,
    data: csvData,
    isLoading: isCSVLoading,
  } = useDriverControllerGetAllCsv(
    {
      queryParams: {
        limit,
        search,
        page,
        sort: sortOptions?.sortBy as
          | "performanceTime"
          | "lastPlow"
          | "lastUpdated",
        order: sortOptions?.direction,
      },
      headers: {
        "Content-Type": "text/csv",
      },
    },
    {
      enabled: false,
    },
  );

  useEffect(() => {
    if (csvData && !isCSVLoading && csvDownloadRequested) {
      // csvData is already a Blob from the fetcher when response is not JSON
      const blob = csvData as unknown as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `drivers-${new Date().toISOString()}.csv`;
      a.click();

      // Reset the download request flag after download
      setCsvDownloadRequested(false);
    }
  }, [csvData, isCSVLoading, csvDownloadRequested]);

  // Handle credentials dialog from navigation state
  useEffect(() => {
    const state = location.state as LocationState;
    if (state?.showCredentialsDialog && state?.credentials) {
      setCredentials(state.credentials);
      setIsPasswordUpdate(state.isPasswordUpdate || false);
      setShowCredentialsDialog(true);
      // Clear the state to prevent showing dialog again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const downloadCSV = () => {
    setCsvDownloadRequested(true);
    execDownload();
  };

  const hasActiveFilters = Boolean(search);
  const { isEmpty, isTrueEmpty } = useListEmptyState(
    isLoading,
    data?.metaData?.count ?? 0,
    hasActiveFilters,
    isError,
  );

  const clearFilters = () => {
    setSearch("");
    setPage(1);
    setSearchParams((prev) => {
      prev.delete("search");
      prev.delete("page");
      return prev;
    });
  };

  const handleDownloadCredentials = () => {
    if (credentials) {
      const credentialsText = `Driver Credentials\n\nUsername: ${credentials.username.toLowerCase()}\nPassword: ${credentials.password}`;
      const blob = new Blob([credentialsText], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `driver-credentials-${credentials.username}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const handleCloseCredentialsDialog = () => {
    setShowCredentialsDialog(false);
    setCredentials(null);
    setIsPasswordUpdate(false);
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Driver Management",
                url: AdRoutes.driverManagement,
              },
            ]}
          />
          <HeaderItemsRight>
            <HeaderStandardActions
              onRefresh={isTrueEmpty ? undefined : refetch}
              onExportCSV={isTrueEmpty ? undefined : downloadCSV}
              onAdd={() => navigate(AdRoutes.addDriver)}
              addButtonText="Add Driver"
            />
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      {!isTrueEmpty && (
        <FilterRow
          tablePaginationProps={{
            limit,
            page,
            isLoading: isLoading,
            count: data?.metaData?.count ?? 10,
            setToPage: setPage,
            setLimit,
          }}
          search={search}
          onSearchChanged={onSearch}
        />
      )}
      {isTrueEmpty ? (
        <Box mt="20px">
          <EmptyState
            title="No Drivers Yet"
            description="New drivers will appear here once you create them"
            actionLabel="Add Driver"
            actionStartIcon={<Add />}
            onAction={() => navigate(AdRoutes.addDriver)}
          />
        </Box>
      ) : isEmpty ? (
        <Box mt="20px">
          <EmptyState {...noResultsEmptyState("drivers", clearFilters)} />
        </Box>
      ) : (
        <DriverManagementTable
          data={data?.data}
          sortOptions={sortOptions}
          sortOptionsChange={setSortOptions}
          isLoading={isLoading}
        />
      )}

      {/* Credentials Success Dialog */}
      <Dialog
        open={showCredentialsDialog}
        onClose={handleCloseCredentialsDialog}
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "400px",
          },
        }}
        fullWidth
      >
        <DialogTitle>
          {isPasswordUpdate ? "Password Updated" : "Driver Created"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {isPasswordUpdate
              ? "The driver will now be logged out of all sessions and must log in again with the new credentials."
              : "Share the credentials with the driver now, or access them later on their profile."}
          </Typography>

          {credentials && (
            <CredentialsBox
              username={credentials.username}
              password={credentials.password}
              showCopyButton
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadCredentials} variant="text">
            Download .txt
          </Button>
          <Button onClick={handleCloseCredentialsDialog} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardContentLayout>
  );
};

export default DriverManagementPage;
